import React, { useEffect, useMemo, useState } from 'react'
import { Stripe, StripeElements } from '@stripe/stripe-js'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import { Stack, Box } from '@mui/material'

import PaymentComponent from '../components/Payment'
import { Stellair } from '../components/Stellair'
import { DesmosPaymentSection } from '../components/DesmosPaymentSection'

import { skipToken } from '@reduxjs/toolkit/query'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { acceptPayment, openStellair, selectChargeAcceptPaymentRequestTracker } from '@/redux/reducers/charge.reducer'
import { selectConsultationById } from '@/redux/reducers/consultation.reducer'
import { configureStripePayment, getStaff, selectConfigureStripePaymentRequestTracker, selectStaffById } from '@/redux/reducers/staff.reducer'

import { isStaff } from '@/utils/type-guards'
import { useSetPriceMutation } from '@/core/services/charge'
import { PaymentType } from '@/core/models/consultation.model'
import { useUpdateConsultationMutation } from '@/core/services/consultation'
import { useGetWaitingRoomQuery } from '@/core/services/waiting-room'
import { useGetNetworkDesmosConfigQuery } from '@/core/services/network'

type PaymentContainerProps = {
  consultationId?: string
}

export function PaymentContainer(props: PaymentContainerProps) {
  const { consultationId } = props
  const appDispatch = useAppDispatch()
  const location = useLocation()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const openStellairRequestHandler = useRequestHandler(
    {
      onSuccess: () => enqueueSnackbar(t('notification.consultation.acceptPayment.success'), { variant: 'success' }),
      onError: () => enqueueSnackbar(t('notification.consultation.acceptPayment.error'), { variant: 'error' }),
      requestTrackerSelector: selectChargeAcceptPaymentRequestTracker,
    }
  )

  const acceptPaymentRequestHandler = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.consultation.acceptPayment.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.consultation.acceptPayment.error'), { variant: 'error' }),
    requestTrackerSelector: selectChargeAcceptPaymentRequestTracker,
  })

  const configureStripePaymentRequestHandler = useRequestHandler({
    onSuccess: () => { },
    onError: () => { },
    requestTrackerSelector: selectConfigureStripePaymentRequestTracker,
  })


  const [setPrice, {
    isLoading: priceLoading
  }] = useSetPriceMutation()

  const authUser = useAppSelector(selectAuthUser)
  const consultation = useAppSelector((state) => (consultationId ? selectConsultationById(state, consultationId) : undefined))
  const doctorId = useMemo(() => consultation?.medecin?._id, [consultation])
  const doctor = useAppSelector(state => doctorId ? selectStaffById(state, doctorId) : undefined)

  const { data: waitingRoom } = useGetWaitingRoomQuery(consultation?.waitingRoom ?? skipToken)
  const networkId = typeof waitingRoom?.network === 'string' ? waitingRoom.network : waitingRoom?.network?._id
  const { data: desmosConfig } = useGetNetworkDesmosConfigQuery(networkId ?? skipToken)

  const [updateConsultation] = useUpdateConsultationMutation()

  useEffect(() => {
    if (!!doctor || !doctorId) return

    appDispatch(getStaff(doctorId))
  }, [appDispatch, doctor, doctorId])

  const handleChangePrice = (price: number) => {
    if (consultationId) {
      setPrice({ consultationId, price })
    }
  }

  const handleConnectOnboarding = () => {
    if (!consultation || !consultation.medecin) return
    configureStripePaymentRequestHandler.dispatch(
      configureStripePayment({
        staffId: consultation.medecin._id,
        refreshUrl: window.location.origin + (location.pathname.startsWith('/') ? location.pathname : `/${location.pathname}`) + '?menu_item=payment',
        returnUrl: window.location.origin + (location.pathname.startsWith('/') ? location.pathname : `/${location.pathname}?`) + '?menu_item=payment',
      })
    )
  }

  const handleStartPayment = ({ stripe, elements }: { stripe: Stripe; elements: StripeElements }) => {
    if (consultationId) {
      acceptPaymentRequestHandler.dispatch(acceptPayment({ consultationId, elements, stripe }))
    }
  }

  const handleOpenStellair = () => {
    if (!consultationId) return
    openStellairRequestHandler.dispatch(openStellair({ consultationId }))
  }

  const handleManualPayment = async (price: number, paymentType: PaymentType) => {
    try {
      if (!consultationId) return
      await updateConsultation({
        id: consultationId,
        changes: {
          price,
          paymentType,
        }
      }).unwrap()

      enqueueSnackbar(t('notification.consultation.validate.success'), { variant: 'success' })
    } catch (error) {
      console.error('Manual payment error:', error)
      enqueueSnackbar(t('notification.consultation.validate.error'), { variant: 'error' })
    }
  }

  if (!consultation) return null

  const waitingRoomId = consultation?.waitingRoom
  const hasDesmosConfigured = !!desmosConfig?.desmosActs?.some(
    (act) => !act.waitingRooms?.length || act.waitingRooms.includes(waitingRoomId!)
  )

  return (
    <>
      <Stack direction="column" sx={{ height: '100%', display: 'flex', alignItems: 'center', padding: '24px', justifyContent: 'center' }}>

        {/* <Stellair onOpenStellair={handleOpenStellair} /> */}
        <Box sx={{ p: 2, width: '100%', maxWidth: 480 }}>
          <Stack spacing={3}>
            <PaymentComponent
              consultationPrice={consultation.price ?? 0}
              paymentType={consultation.paymentType}
              isConfiguringPayment={configureStripePaymentRequestHandler.requestTracker.loading}
              isDoctorStripePaymentActivated={!!doctor?.stripeActivated}
              isDoctor={isStaff(authUser) && authUser?.status === 'MEDECIN'}
              isPaid={!!consultation.paid}
              isPaying={acceptPaymentRequestHandler.requestTracker.loading}
              isUpdatingPrice={priceLoading}
              onChangePrice={handleChangePrice}
              onConnectOnboarding={handleConnectOnboarding}
              onStartPayment={handleStartPayment}
              receiptUrl={consultation.receiptUrl}
              onManualPayment={handleManualPayment}
            />

            {hasDesmosConfigured && consultationId && (
              <DesmosPaymentSection
                consultationId={consultationId}
                consultationStatus={consultation.status}
                desmosActStatus={consultation.desmosActStatus}
                waitingRoomId={consultation.waitingRoom}
                networkId={networkId}
              />
            )}
          </Stack>
        </Box>
      </Stack>
    </>
  )
}

export default PaymentContainer
